import { DetailSaveButton } from './DetailSaveButton';

/**
 * Which document ArchiPro renders on request for a page. A page can offer more
 * than one, so naming the page is not enough to say what was saved.
 */
export type GeneratedDocument = 'TearSheet' | 'Lookbook';

/**
 * What the row holds. A file has its own record to pin; a generated document
 * has none - nothing is stored for it - so it is pinned by the page it is made
 * from, plus which of that page's documents it is.
 */
type SavedDownload =
    | { kind: 'file'; fileId: number }
    | { kind: 'generated'; document: GeneratedDocument };

type DownloadSaveButtonProps = SavedDownload & {
    /** Named in the control's label, so the action says what it saves. */
    title: string;
    /** The page the file hangs off, and the one a generated document is made from. */
    itemId: number;
    /** Size override, for a list whose rows are shorter than a detail CTA. */
    className?: string;
    /**
     * Passed through. `white` for a row whose own download control is white on
     * a tinted page; `outline` where the row itself is white and a white button
     * would disappear into it.
     */
    tone?: 'outline' | 'white';
    /**
     * Run before the board picker opens. Carries the gated file's lead, which
     * downloading would otherwise be the only way to raise.
     */
    onSaveIntent?: () => void;
};

/**
 * Saves a download to a design board, beside whatever control downloads it.
 *
 * The detail pages' save button, so the control reads the same here as it does
 * on a product or a professional: a bookmark that grows its label out on hover,
 * and shows the file as saved once it is.
 *
 * Shared by the surfaces that list files: a professional's Downloads tab and a
 * product's Downloads section.
 */
export const DownloadSaveButton = ({
    title,
    itemId,
    className,
    tone = 'outline',
    onSaveIntent,
    ...saved
}: DownloadSaveButtonProps) => (
    <DetailSaveButton
        pinId={saved.kind === 'generated' ? itemId : saved.fileId}
        pinType={
            saved.kind === 'generated' ? `${saved.document}Pin` : 'DownloadPin'
        }
        hoverLabel
        ariaLabel={(isSaved) =>
            `${isSaved ? 'Saved' : 'Save'} ${title} to a design board`
        }
        tone={tone}
        className={className}
        onSaveIntent={onSaveIntent}
    />
);
